import { useRef } from 'react';
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { Input, Switch, Textarea } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';
import { AdvanceSlider } from './VoiceAgentAdvanceSlider';

interface VoiceAgentFillerWordsSectionProps {
  control: Control<EditAgentFormValues>;
  setValue: UseFormSetValue<EditAgentFormValues>;
}

export function VoiceAgentFillerWordsSection({
  control,
  setValue,
}: Readonly<VoiceAgentFillerWordsSectionProps>) {
  const enabled = useWatch({ control, name: 'filler_words.enabled', defaultValue: false });
  const phrases =
    useWatch({ control, name: 'filler_words.filler_phrases', defaultValue: [] }) ?? [];
  const nextPhraseKey = useRef(0);
  const phraseKeys = useRef<string[]>([]);
  while (phraseKeys.current.length < phrases.length) {
    phraseKeys.current.push(`filler-phrase-${nextPhraseKey.current++}`);
  }
  phraseKeys.current.length = phrases.length;

  const updatePhrases = (nextPhrases: string[]) => {
    setValue('filler_words.filler_phrases', nextPhrases, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <div className="rounded-xl border border-divider bg-content1">
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-default-100 p-2 text-primary">
            <Icon icon="solar:chat-round-dots-linear" width={20} />
          </div>
          <div>
            <p className="font-medium text-foreground">Filler Words</p>
            <p className="mt-0.5 text-sm text-default-500">
              Play brief acknowledgments while the agent is thinking.
            </p>
          </div>
        </div>
        <Controller
          name="filler_words.enabled"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Switch
              aria-label="Enable filler words"
              isSelected={field.value ?? false}
              onValueChange={field.onChange}
            />
          )}
        />
      </div>

      {enabled && (
        <div className="space-y-5 border-t border-divider p-4">
          <Controller
            name="filler_words.delay_seconds"
            control={control}
            defaultValue={0.7}
            render={({ field }) => (
              <AdvanceSlider
                label="Delay seconds"
                minValue={0}
                maxValue={4}
                step={0.1}
                value={field.value ?? 0.7}
                onChange={field.onChange}
                valueSuffix=" s"
              />
            )}
          />

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-foreground">Filler phrases</p>
                <p className="text-xs text-default-500">
                  Phrases spoken during processing pauses.
                </p>
              </div>
              <CtyButton
                size="sm"
                variant="flat"
                color="primary"
                startContent={<Icon icon="solar:add-circle-linear" width={18} />}
                onPress={() => updatePhrases([...phrases, ''])}
              >
                Add phrase
              </CtyButton>
            </div>

            {phrases.length === 0 ? (
              <div className="rounded-lg border border-dashed border-divider px-4 py-5 text-center text-sm text-default-500">
                No filler phrases added.
              </div>
            ) : (
              <div className="space-y-2">
                {phrases.map((phrase, index) => (
                  <div key={phraseKeys.current[index]} className="flex items-center gap-2">
                    <Input
                      aria-label={`Filler phrase ${index + 1}`}
                      placeholder={`Phrase ${index + 1}`}
                      value={phrase}
                      onValueChange={(value) =>
                        updatePhrases(phrases.map((item, itemIndex) => (itemIndex === index ? value : item)))
                      }
                      variant="bordered"
                      className="flex-1"
                      classNames={{ inputWrapper: 'min-h-10' }}
                    />
                    <CtyButton
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="danger"
                      aria-label={`Delete phrase ${index + 1}`}
                      onPress={() => {
                        phraseKeys.current.splice(index, 1);
                        updatePhrases(phrases.filter((_, itemIndex) => itemIndex !== index));
                      }}
                    >
                      <Icon icon="solar:trash-bin-trash-linear" width={18} />
                    </CtyButton>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-lg border border-divider bg-default-50 p-3">
            <div>
              <p className="text-sm font-medium text-foreground">Small LLM config</p>
              <p className="text-xs text-default-500">
                Model used to generate short filler acknowledgments.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Controller
                name="filler_words.sllm_config.provider"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Provider"
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    variant="bordered"
                    aria-label="Filler words LLM provider"
                    isDisabled
                  />
                )}
              />
              <Controller
                name="filler_words.sllm_config.model"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Model"
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    variant="bordered"
                    aria-label="Filler words LLM model"
                    isDisabled
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Controller
                name="filler_words.sllm_config.temperature"
                control={control}
                defaultValue={0.7}
                render={({ field }) => (
                  <AdvanceSlider
                    label="Temperature"
                    minValue={0}
                    maxValue={1}
                    step={0.1}
                    value={field.value ?? 0.7}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="filler_words.sllm_config.max_completion_tokens"
                control={control}
                render={({ field }) => (
                  <Input
                    type="number"
                    label="Max tokens"
                    value={field.value != null ? String(field.value) : ''}
                    onValueChange={(value) => {
                      if (value.trim() === '') {
                        field.onChange(undefined);
                        return;
                      }
                      const n = Number(value);
                      field.onChange(Number.isFinite(n) ? Math.max(0, Math.round(n)) : undefined);
                    }}
                    min={0}
                    variant="bordered"
                    aria-label="Filler words max tokens"
                  />
                )}
              />
            </div>
            <Controller
              name="filler_words.sllm_config.system_prompt"
              control={control}
              render={({ field }) => (
                <Textarea
                  label="System prompt"
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  minRows={3}
                  variant="bordered"
                  aria-label="Filler words system prompt"
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
